/**
 * STORY#3197 专业版线上购买 —— 常量（文案 / 枚举 / 状态映射）
 * 所有页面文案与枚举集中于此，便于后续接口接入时替换数据源而不改动组件。
 */

// 与老端 frp_company_web 约定的跳转路由（老端 PRO_PLAN_ROUTES 同值）
// 老端拼的是 /web-client/proPlan/xxx，本端路由 base 即 /web-client/
export const PRO_PLAN_ROUTES = {
  plan: "/proPlan/index", // 套餐购买页
  orderList: "/proPlan/orderList", // 专业版订单列表页
  rights: "/proPlan/rights", // 专业版权益页（区域未开通线上支付 → 联系客服）
  rightsDetail: "/proPlan/rightsDetail", // 专业版权益详情页（9 大权益说明）
};

// 协议类型（与老端 AGREEMENT_TYPE 对齐，见老端 src/js/common/urlconfig.js）
export const AGREEMENT_TYPE = {
  USER: 1, // 用户协议
  PRIVACY: 2, // 隐私政策
  PRO_PLAN: 20, // 神农口袋专业版服务协议
};

// 订单状态（后端 payment 服务枚举，见 /api/payment/v2/api-docs?group=coApi）
export const ORDER_STATUS = {
  UNPAID: 10, // 待支付
  PAID: 20, // 已支付
  CANCELED: 30, // 已取消
  CLOSED: 40, // 已关闭（订单列表接口不返回该状态）
  REFUNDING: 50, // 退款中
  REFUND_FAILED: 60, // 退款失败
  REFUNDED: 70, // 已退款
};

export const ORDER_STATUS_TEXT = {
  [ORDER_STATUS.UNPAID]: "待支付",
  [ORDER_STATUS.PAID]: "已支付",
  [ORDER_STATUS.CANCELED]: "已取消",
  [ORDER_STATUS.CLOSED]: "已关闭",
  [ORDER_STATUS.REFUNDING]: "退款中",
  [ORDER_STATUS.REFUND_FAILED]: "退款失败",
  [ORDER_STATUS.REFUNDED]: "已退款",
};

// 状态角标样式（对应 StatusRibbon 的 class，渐变见 §5.2-F）
export const ORDER_STATUS_CLASS = {
  [ORDER_STATUS.UNPAID]: "ribbon-unpaid",
  [ORDER_STATUS.PAID]: "ribbon-paid",
  [ORDER_STATUS.CANCELED]: "ribbon-canceled",
  [ORDER_STATUS.CLOSED]: "ribbon-canceled",
  [ORDER_STATUS.REFUNDING]: "ribbon-refunding",
  [ORDER_STATUS.REFUND_FAILED]: "ribbon-refund-failed",
  [ORDER_STATUS.REFUNDED]: "ribbon-refunded",
};

// 状态附加提示（角标外的橙色/灰色说明行）
export const ORDER_STATUS_TIP = {
  [ORDER_STATUS.REFUNDING]: "退款处理中，预计1-3个工作日到账",
};

// 支付方式（paymentOrder 的 payMethod）
export const PAY_METHOD = {
  WECHAT: 1, // 微信支付
  TRANSFER: 2, // 对公转账
};

export const PAY_METHOD_TEXT = {
  [PAY_METHOD.WECHAT]: "微信支付",
  [PAY_METHOD.TRANSFER]: "对公转账",
};

// 支付方式查询的终端类型（paymentMethodConfig/list 的 terminal）
export const PAY_TERMINAL = {
  WEB: 1,
  APP: 2,
};

// 订单来源（paymentOrder 的 orderSource）
// 取值以管理后台 frp_admin_web 的 professionalOrder/constants.js 为准：
//   1 Web端 / 2 安卓端 / 3 运营 / 4 系统
// 其中 1、2 为**线上单**（有支付链接，可在任意端扫码支付）；3、4 为**线下单**
//（运营后台代下单，无线上支付链接）。
export const ORDER_SOURCE = {
  WEB: 1, // Web 端下单
  APP: 2, // 安卓 App 下单
  OFFLINE_OPERATE: 3, // 运营后台下单（线下）
  OFFLINE_SYSTEM: 4, // 系统下单（线下）
};
// 线下单来源（无线上支付链接）：在 web 端无法继续支付
export const OFFLINE_ORDER_SOURCES = [
  ORDER_SOURCE.OFFLINE_OPERATE,
  ORDER_SOURCE.OFFLINE_SYSTEM,
];

// 企业购买资格校验失败原因（paymentOrder/qualification 的 reasonCode）
export const QUALIFY_REASON = {
  CHARGE_CLOSED: 41003, // 收费开关关闭 → 不展示购买入口
  AREA_CLOSED: 41004, // 区域未开放线上支付 → 跳专业版权益页
  NO_FARM: 41005, // 企业无农场 → 展示 reasonDesc
};

// 业务错误码
export const PRO_PLAN_ERROR = {
  HAS_UNPAID_ORDER: 42001, // 存在待支付订单 → 弹「继续下单 / 前往支付」
  ORDER_NOT_EXIST: 42002, // 订单不存在或不属于当前企业（文档未列，联调实测遇到）
  ORDER_PAID: 42003, // 订单已支付 → 直接跳订单列表
};

// 主动查单结果（paymentOrder/verifyPayStatus 的 payResult）
export const PAY_RESULT = {
  SUCCESS: "SUCCESS", // 支付成功
  PROGRESS: "PROGRESS", // 渠道处理中（不可判失败，需继续等待）
  FAIL: "FAIL", // 支付失败
};

// 页面文案
export const PRO_PLAN_TEXT = {
  planTitle: "套餐购买",
  planMainTitle: "升级专业版解锁更多能力",
  planSubTitle: "如果我们的产品好用，且您希望长期使用，请购买专业版",
  rightsTitle: "专业版权益",
  rightsTableTitle: "专业版9大权益升级",
  viewRightsDetail: "查看权益详情",
  upgradeNow: "立即升级",
  contactKefu: "联系客服",
  payScanTip: "请使用 微信 扫描二维码完成支付",
  payCountdownLabel: "剩余支付时间：",
  agreePrefix: "我已阅读并同意",
  agreeName: "《神农口袋专业版服务协议》",
  agreeLockTip: "同意协议后扫码支付",
  agreeBtn: "同意协议",
  orderExpired: "当前订单已过期",
  backWorkbench: "返回工作台",
  backToWorkbench: "回到工作台",
  reorder: "重新下单",
  paySuccess: "支付成功",
  paySuccessDesc: "您的专业版已开通",
  payFailed: "支付失败",
  payFailedDesc: "当前订单支付失败，请重新支付",
  viewOrder: "查看订单",
  repay: "重新支付",
  paid: "我已支付",
  cancel: "取消",
  queryingPayResult: "正在查询支付结果",
  // 查单结果为「处理中」时的提示（不判失败）
  payProgress: "支付结果确认中，请稍后查看",
  confirmPay: "确认支付",
  confirmPayDesc: "未检测到您的支付结果请确认您是否已经支付完成?",
  unpaidOrderTitle: "提示",
  unpaidOrderDesc:
    "您有待支付订单，请选择继续下单，或前往支付已有订单，需注意，选择继续下单将关闭已有订单",
  continueOrder: "继续下单",
  goPay: "前往支付",
  orderInfo: "订单信息",
  companyLabel: "开通企业",
  goodsLabel: "商品",
  durationLabel: "时长",
  validPeriodLabel: "有效期",
  realPayLabel: "实付价",
  proPrefix: "专业版",
  totalLabel: "合计",
  cancelOrder: "取消订单",
  cancelOrderConfirm: "确定要取消该订单吗？",
  cancelSuccess: "订单已取消",
  cancelFailed: "取消失败，请稍后重试",
  // 取消时订单已被支付（42003）
  orderAlreadyPaid: "该订单已支付，无法取消",
  // 取消时订单不存在/不属于当前企业（42002）
  orderNotExist: "订单不存在或已变更，请刷新后重试",
  // 列表页未取到待支付单的引导
  reorderFromList: "请重新选择套餐完成支付",
  // App 端下单的待支付单在 web 端无法支付（禅道 #23497）：
  // App 走的是「App 内拉起微信支付」（prepayInfo），与 web 扫码用的 codeUrl 不是一回事，
  // 取不到可渲染的二维码，故需明确告知用户回 App 支付或取消后重下
  appOrderCannotPayInWeb:
    "这笔订单不能在当前环境支付哦，请前往下单时的APP付款，或取消订单后重新下单",
  goPayOrder: "去支付",
  orderListTitle: "专业版订单",
  emptyText: "暂无数据",
  // 咨询客服弹框（与老端 initKefu 文案一致）
  kefuTitle: "咨询客服",
  kefuWechatTip: "加客服微信咨询",
  kefuPhoneTip: "电话咨询",
  kefuUnavailable: "抱歉，暂时无法联系客服",
};

// 状态条（免费版/专业版）文案
export const PRO_BANNER_TEXT = {
  freeTitle: "免费版",
  // 免费版副标题：与设计稿 1877:657 一致（禅道 #23420-2，产品确认用这版）
  freeDesc: "升级专业版可无限开合格证、享更多特权",
  freeBtn: "去升级",
  proTitle: "专业版",
  proBtn: "去续费",
  proExpiredDesc: "您的专业版已过期，请及时续费",
  endTimeSuffix: "到期",
  autoRenewSuffix: "（购买后自动顺延）",
  // 专业版有效且剩余 >30 天：状态条右侧展示的权益标签（设计稿 1994-60）
  proActiveTag: "权益尊享中",
};

// 套餐时长换算天数（需求 §4：月 30 / 季 90 / 半年 180 / 年 365）
export const PLAN_UNIT_DAYS = {
  month: 30,
  quarter: 90,
  halfYear: 180,
  year: 365,
};

/* ------------------ 其他配置 ------------------ */

/**
 * 支付倒计时（分钟）：与后端订单有效期一致（30 分钟）。
 *
 * 该值同时决定两件事：
 *   1. 接口未返回 expireTime 时的兜底时长；
 *   2. **倒计时展示上限**（见 utils/proPlan.js 的 capPayCountdown）——
 *      与后端一致时上限不截断任何值，capPayCountdown 退化为无操作。
 *      归零后确认页会查一次订单状态：后端仍待支付则按服务器时间重新计时（见
 *      OrderConfirm.handleCountdownEnd），不会误判订单过期。
 *
 * 历史备注：2026-09-21 测试期曾临时压到 5 分钟以快速联调「订单过期」流程，
 * 2026-09-24 已改回 30（禅道验收项）。
 */
export const PAY_COUNTDOWN_MINUTES = 30;
export const PAY_POLL_INTERVAL = 2000;

/**
 * 倒计时归零后的状态确认重试（见 OrderConfirm.handleCountdownEnd）。
 * 后端关单/支付状态可能异步落库，归零瞬间首次查单未必是最新状态，故失败、
 * 或「仍待支付但服务端时间已到」时按此间隔重试，仍无结论才按过期处理。
 */
export const PAY_STATUS_CONFIRM_ATTEMPTS = 3;
export const PAY_STATUS_CONFIRM_INTERVAL = 1000;

/**
 * 订单列表卡片倒计时归零后的核对策略（见 OrderList.handleExpired）。
 * 后端到期关单并非瞬时：实测订单 14:06:56 到期、列表接口在 14:07:19 仍返回「待支付」，
 * 随后才变「已取消」（另一笔实测延迟约 9s），故列表刷新要重试若干次才能拿到终态。
 * 若这段时间内后端仍报待支付且截止时间未到，则回填真实剩余时长继续倒计时。
 */
export const ORDER_EXPIRE_CONFIRM_ATTEMPTS = 6;
export const ORDER_EXPIRE_CONFIRM_INTERVAL = 2000;
// 核对未果（后端迟迟未关单）时，卡片按此间隔再次发起核对，避免永久停在 00′00″
export const ORDER_EXPIRE_RECHECK_INTERVAL = 10000;
export const ORDER_EXPIRE_RECHECK_MAX = 30;

// 专业版有效期剩余天数阈值（≤30 天展示「去续费」）
export const RENEW_TIP_DAYS = 30;

/**
 * 支付倒计时兜底秒数：接口未返回 expireTime 时使用；
 * 同时作为倒计时展示上限（测试期压短倒计时依赖此值，见 PAY_COUNTDOWN_MINUTES 说明）。
 */
export const PAY_COUNTDOWN_DEFAULT_SECONDS = PAY_COUNTDOWN_MINUTES * 60;

// 订单列表分页大小
export const ORDER_PAGE_SIZE = 20;
